import { useEffect, useState, useCallback, useRef } from "react";
import {
  History, MessageCircle, Mail, CheckCircle2, Loader2, XCircle, Clock,
  ChevronDown, Pause, CalendarClock, Timer, FileText, AlertTriangle, Phone, Calendar,
} from "lucide-react";
import api from "@/lib/api";
import { Card, Badge, Spinner } from "@/components/kit";
import Pagination from "@/components/Pagination";
import BlastCalendar from "@/components/BlastCalendar";

const STATUS = {
  done:      { c: "green", label: "Selesai",    icon: CheckCircle2 },
  running:   { c: "sky",   label: "Berjalan",   icon: Loader2 },
  queued:    { c: "amber", label: "Menunggu",   icon: Clock },
  scheduled: { c: "violet",label: "Terjadwal",  icon: CalendarClock },
  paused:    { c: "amber", label: "Dijeda",     icon: Pause },
  failed:    { c: "red",   label: "Gagal",      icon: XCircle },
  cancelled: { c: "slate", label: "Dibatalkan", icon: XCircle },
};

const LOG_STATUS = {
  sent:     { c: "green", label: "Terkirim" },
  failed:   { c: "red",   label: "Gagal" },
  cooldown: { c: "amber", label: "Cooldown 7d" },
  no_email: { c: "slate", label: "Tanpa email" },
  no_phone: { c: "slate", label: "Tanpa nomor" },
};

const PER = 8;

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso; }
}
function fmtDuration(sec) {
  if (!sec || sec < 1) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60), s = sec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}j ${m % 60}m`;
}

export default function BlastHistory() {
  const [jobs, setJobs] = useState(null);
  const [page, setPage] = useState(1);
  const [openLogs, setOpenLogs] = useState(null);
  const [detail, setDetail] = useState({ logs: [], stats: null, loading: false });
  const [logFilter, setLogFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // "list" | "calendar"
  const timer = useRef(null);

  const load = useCallback(
    () => api.get("/blast/jobs").then((r) => setJobs(r.data.jobs)).catch(() => setJobs([])),
    []
  );

  useEffect(() => {
    load();
    timer.current = setInterval(load, 4000); // live monitor across navigation
    return () => clearInterval(timer.current);
  }, [load]);

  const viewLogs = async (id) => {
    if (openLogs === id) { setOpenLogs(null); return; }
    setOpenLogs(id);
    setDetail({ logs: [], stats: null, loading: true });
    setLogFilter("all");
    try {
      const { data } = await api.get(`/blast/job/${id}/logs`);
      setDetail({ logs: data.logs || [], stats: data.stats || null, loading: false });
    } catch {
      setDetail({ logs: [], stats: null, loading: false });
    }
  };

  const totalPages = jobs ? Math.ceil(jobs.length / PER) : 1;
  const rows = jobs ? jobs.slice((page - 1) * PER, page * PER) : [];
  const active = jobs ? jobs.filter((j) => ["running", "queued"].includes(j.status)).length : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-indigo-50 grid place-items-center">
          <History className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <span className="inline-flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-sky-100 grid place-items-center shrink-0">
              <History className="w-5 h-5 text-sky-600" />
            </span>
            <h1 className="font-display text-3xl font-extrabold">Riwayat Blast</h1>
          </span>
          <p className="text-slate-500 mt-0.5 text-sm">
            Detail per pesan — kapan dikirim, status, dan delay aktual. Status diperbarui otomatis.
          </p>
        </div>
        {active > 0 && (
          <Badge color="sky" className="ml-auto">
            <Loader2 className="w-3 h-3 animate-spin" /> {active} proses berjalan
          </Badge>
        )}
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <button onClick={() => setViewMode("list")} className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${viewMode === "list" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <History className="w-4 h-4 inline mr-1.5" /> List
        </button>
        <button onClick={() => setViewMode("calendar")} className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${viewMode === "calendar" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <Calendar className="w-4 h-4 inline mr-1.5" /> Kalender
        </button>
      </div>

      {viewMode === "calendar" ? (
        <BlastCalendar />
      ) : jobs === null ? (
        <Spinner className="w-7 h-7 mx-auto my-16" />
      ) : jobs.length === 0 ? (
        <Card className="p-12 text-center text-slate-400" data-testid="history-empty">
          <History className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          Belum ada riwayat blast.
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((j) => {
            const st = STATUS[j.status] || STATUS.queued;
            const Ch = j.blast_type === "email" ? Mail : MessageCircle;
            const pending = Math.max((j.total_leads || 0) - j.sent - j.failed - j.skipped, 0);
            return (
              <Card key={j.id} className="p-4" data-testid="blast-history-row">
                {/* Header row */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className={`w-9 h-9 rounded-lg grid place-items-center ${j.blast_type === "email" ? "bg-violet-50 text-violet-600" : "bg-emerald-50 text-emerald-600"}`}>
                    <Ch className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">
                      {j.blast_type === "email" ? "Email" : "WhatsApp"} · {j.template_name}
                    </p>
                    <p className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
                      <span>{fmtTime(j.created_at)}</span>
                      {j.duration_seconds > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Timer className="w-3 h-3" /> {fmtDuration(j.duration_seconds)}
                        </span>
                      )}
                      {j.blast_type !== "email" && j.delay_avg > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> ~{j.delay_avg}s delay
                        </span>
                      )}
                    </p>
                  </div>
                  <Badge color={st.c} data-testid="blast-status">
                    <st.icon className={`w-3 h-3 ${j.status === "running" ? "animate-spin" : ""}`} /> {st.label}
                  </Badge>
                </div>

                {/* Pause reason (safety auto-pause) */}
                {j.status === "paused" && j.pause_reason && (
                  <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{j.pause_reason}</span>
                  </div>
                )}

                {/* Template snippet */}
                {j.template_snippet && (
                  <div className="mt-3 bg-slate-50 rounded-lg p-2.5 text-xs text-slate-600 line-clamp-2 italic">
                    <FileText className="w-3 h-3 inline mr-1 text-slate-400" />
                    {j.template_snippet}
                  </div>
                )}

                {/* Progress bar */}
                <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${j.blast_type === "email" ? "bg-violet-500" : "bg-emerald-500"}`}
                    style={{ width: `${j.progress_pct}%` }}
                  />
                </div>

                {/* Counters */}
                <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                  {[
                    ["Total", j.total_leads, "slate"],
                    ["Sukses", j.sent, "emerald"],
                    ["Pending", pending, "amber"],
                    ["Gagal", j.failed, "rose"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="bg-slate-50 rounded-lg py-1.5">
                      <p className={`font-bold text-${c}-600`}>{v}</p>
                      <p className="text-[11px] text-slate-400">{l}</p>
                    </div>
                  ))}
                </div>

                {/* Toggle */}
                <button
                  onClick={() => viewLogs(j.id)}
                  data-testid="blast-logs-toggle"
                  className="mt-3 text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1"
                >
                  Detail log per pesan
                  <ChevronDown className={`w-3 h-3 transition-transform ${openLogs === j.id ? "rotate-180" : ""}`} />
                </button>

                {/* Expanded detail */}
                {openLogs === j.id && (
                  <div className="mt-3 space-y-3" data-testid="blast-logs">
                    {/* Stats strip */}
                    {detail.stats && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-emerald-50 text-emerald-700 rounded-lg px-3 py-2">
                          <p className="font-bold text-base">{detail.stats.sent}</p>
                          <p className="text-[11px] opacity-80">terkirim</p>
                        </div>
                        <div className="bg-rose-50 text-rose-700 rounded-lg px-3 py-2">
                          <p className="font-bold text-base">{detail.stats.failed}</p>
                          <p className="text-[11px] opacity-80">gagal</p>
                        </div>
                        <div className="bg-amber-50 text-amber-700 rounded-lg px-3 py-2">
                          <p className="font-bold text-base">{detail.stats.cooldown}</p>
                          <p className="text-[11px] opacity-80">cooldown 7 hari</p>
                        </div>
                        <div className="bg-sky-50 text-sky-700 rounded-lg px-3 py-2">
                          <p className="font-bold text-base">
                            {detail.stats.avg_delay > 0 ? `${detail.stats.avg_delay}s` : "—"}
                          </p>
                          <p className="text-[11px] opacity-80">
                            delay rata-rata
                            {detail.stats.min_delay_used > 0 && (
                              <> ({detail.stats.min_delay_used}–{detail.stats.max_delay_used}s)</>
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Filter chips */}
                    {detail.logs.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                        {[
                          ["all", "Semua", detail.logs.length],
                          ["sent", "Terkirim", detail.stats?.sent || 0],
                          ["failed", "Gagal", detail.stats?.failed || 0],
                          ["cooldown", "Cooldown", detail.stats?.cooldown || 0],
                        ].map(([k, label, n]) => (
                          <button
                            key={k}
                            onClick={() => setLogFilter(k)}
                            data-testid={`log-filter-${k}`}
                            className={`px-2.5 py-1 rounded-full font-semibold ring-1 ring-inset transition ${
                              logFilter === k
                                ? "bg-indigo-600 text-white ring-indigo-600"
                                : "bg-white text-slate-600 ring-slate-200 hover:ring-indigo-300"
                            }`}
                          >
                            {label} <span className="opacity-70">({n})</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Log list */}
                    <div className="rounded-lg border border-slate-100 divide-y divide-slate-50 max-h-96 overflow-y-auto bg-white">
                      {detail.loading ? (
                        <p className="p-4 text-xs text-slate-400 text-center">
                          <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" /> Memuat log…
                        </p>
                      ) : detail.logs.length === 0 ? (
                        <p className="p-4 text-xs text-slate-400 text-center">Belum ada log untuk job ini.</p>
                      ) : (() => {
                        const filtered = logFilter === "all"
                          ? detail.logs
                          : detail.logs.filter((l) => l.status === logFilter);
                        if (filtered.length === 0) {
                          return <p className="p-4 text-xs text-slate-400 text-center">Tidak ada log pada filter ini.</p>;
                        }
                        return filtered.map((l, i) => {
                          const ls = LOG_STATUS[l.status] || { c: "slate", label: l.status };
                          return (
                            <div key={i} className="px-3 py-2.5 text-xs hover:bg-slate-50/60 transition" data-testid="blast-log-row">
                              <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-slate-700 truncate max-w-[180px]">
                                      {l.business_name || "—"}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-slate-500">
                                      <Phone className="w-3 h-3" /> {l.target_number || "—"}
                                    </span>
                                    {l.city && <span className="text-slate-400">· {l.city}</span>}
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
                                    <span className="inline-flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {fmtTime(l.sent_at)}
                                    </span>
                                    {l.delay_seconds > 0 && (
                                      <span className="inline-flex items-center gap-1">
                                        <Timer className="w-3 h-3" /> delay {l.delay_seconds}s
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Badge color={ls.c} data-testid="blast-log-status">{ls.label}</Badge>
                              </div>

                              {l.message_preview && (
                                <p className="mt-1.5 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1 line-clamp-2 whitespace-pre-line">
                                  {l.message_preview}
                                </p>
                              )}
                              {l.status === "failed" && l.detail && (
                                <p className="mt-1 text-[11px] text-rose-600 bg-rose-50 rounded px-2 py-1 line-clamp-2">
                                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                                  {l.detail}
                                </p>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}
